import { useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { NoteAdd as NoteAddIcon } from "@mui/icons-material";
import { Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth, useOutletApi } from "@/features/authentication";
import { UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { formatDate, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  PageContainer,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

// Purchase returns other outlets raised against this one and that are still
// waiting to be mirrored as a sales return. The hub sits on this queue: billing
// on the source receipt stays frozen until every return on it is converted.
export const IncomingPurchaseReturns = () => {
  const { outlet, changeOutlet, isHubOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const api = useOutletApi();
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const loadIncomingPurchaseReturns = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await api.get("purchase-returns/incoming", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { keyword }),
        },
      });
      setPurchaseReturns(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat retur pembelian masuk"));
    } finally {
      setLoading(false);
    }
  }, [
    api,
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadIncomingPurchaseReturns();
  }, [loadIncomingPurchaseReturns]);

  return (
    <PageContainer
      title="Retur dari Pembeli"
      breadcrumbs={[]}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "flex-end", alignItems: "end" }}
        >
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Penjual",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. Referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={purchaseReturns}
          loading={loading}
          getRowHeight={() => "auto"}
          slots={{
            noRowsOverlay: () => (
              <Stack
                sx={{
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 3,
                }}
              >
                {isHubOutlet
                  ? "Tidak ada retur pembelian yang menunggu konversi"
                  : "Retur pembelian masuk hanya diterima oleh outlet Hub"}
              </Stack>
            ),
          }}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              // The age matters here: an unconverted return freezes invoicing
              // on its source receipt.
              valueGetter: (transactionDate) => {
                const today = dayjs().startOf("day");
                const transaction = dayjs(transactionDate).startOf("day");
                const age = today.diff(transaction, "day");
                return age > 0
                  ? `${formatDate(transactionDate)} (${age}d)`
                  : formatDate(transactionDate);
              },
            },
            {
              field: "buyer",
              headerName: "Outlet Peretur",
              flex: 1.5,
              sortable: false,
              valueGetter: (buyer) => buyer?.name ?? "-",
            },
            {
              field: "returnable",
              headerName: "Retur Dari",
              flex: 1.5,
              sortable: false,
              valueGetter: (returnable) => returnable?.referenceNumber ?? "-",
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1.5,
              sortable: false,
              valueGetter: (note) => note ?? "-",
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row: { referenceNumber } }) => {
                const actions = [];
                if (hasPermission(PermissionEnum.CREATE_SALES_RETURNS)) {
                  actions.push(
                    <Tooltip key="convert" title="Buat Retur Penjualan">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`${referenceNumber}/create-sales-return`)
                        }
                      >
                        <NoteAddIcon />
                      </IconButton>
                    </Tooltip>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
